import React, { useState, useEffect, useRef } from "react";
import { clientService } from "../services/client";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faSpinner } from "@fortawesome/free-solid-svg-icons";

const AutocompleteClient = ({ 
  onClientSelect, 
  selectedClient = null,
  placeholder = "Digite o nome do cliente...",
  className = "",
  disabled = false 
}) => {
  const [searchTerm, setSearchTerm] = useState(selectedClient?.nome || "");
  const [clients, setClients] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (selectedClient) {
      setSearchTerm(selectedClient.nome);
    }
  }, [selectedClient]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const searchClients = async (term) => {
    if (term.trim().length < 1) {
      setClients([]);
      return;
    }

    try {
      setLoading(true);
      setError("");
      const data = await clientService.searchClients(term.trim());
      setClients(data);
    } catch (err) {
      setError(err.message || "Erro ao buscar clientes");
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowDropdown(true);
    
    // Clear selected client if user is typing
    if (selectedClient && value !== selectedClient.nome) {
      onClientSelect(null);
    }

    // Debounce search - reduzido para resposta mais rápida
    const timeoutId = setTimeout(() => {
      searchClients(value);
    }, 200);

    return () => clearTimeout(timeoutId);
  };

  const handleClientSelect = (client) => {
    setSearchTerm(client.nome);
    setShowDropdown(false);
    onClientSelect(client);
  };

  const handleInputFocus = () => {
    setShowDropdown(true);
    if (searchTerm.length >= 1) {
      searchClients(searchTerm);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setShowDropdown(false);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full px-2 sm:px-3 py-1.5 sm:py-2 pr-8 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-400 text-xs sm:text-sm disabled:bg-neutral-100 disabled:cursor-not-allowed"
        />
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          {loading ? (
            <FontAwesomeIcon 
              icon={faSpinner} 
              className="h-4 w-4 text-neutral-400 animate-spin" 
            />
          ) : (
            <FontAwesomeIcon 
              icon={faSearch} 
              className="h-4 w-4 text-neutral-400" 
            />
          )}
        </div>
      </div>

      {/* Dropdown */}
      {showDropdown && !disabled && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-neutral-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
        >
          {error && (
            <div className="px-3 py-2 text-sm text-red-600 bg-red-50">
              {error}
            </div>
          )}
          
          {clients.length === 0 && !loading && !error && searchTerm.length >= 1 && (
            <div className="px-3 py-2 text-sm text-neutral-500">
              {/^\d+$/.test(searchTerm.trim()) 
                ? `Nenhum cliente encontrado com NIF "${searchTerm}"`
                : `Nenhum cliente encontrado com nome "${searchTerm}"`
              }
            </div>
          )}

          {clients.map((client) => (
            <div
              key={client.id}
              onClick={() => handleClientSelect(client)}
              className="px-3 py-2 cursor-pointer hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0"
            >
              <div className="font-medium text-sm text-neutral-900">
                {client.nome}
                {client.nif && (
                  <span className="ml-2 text-xs text-neutral-500 font-normal">
                    (NIF: {client.nif})
                  </span>
                )}
              </div>
              {client.empresa && (
                <div className="text-xs text-neutral-600">
                  📍 {client.empresa}
                </div>
              )}
              {client.zona && (
                <div className="text-xs text-neutral-500">
                  🗺️ {client.zona}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Selected client info */}
      {selectedClient && (
        <div className="mt-2 p-2 bg-neutral-50 rounded-md border border-neutral-200">
          <div className="text-sm font-medium text-neutral-900">
            {selectedClient.nome}
          </div>
          {selectedClient.empresa && (
            <div className="text-xs text-neutral-600">
              Empresa: {selectedClient.empresa}
            </div>
          )}
          {selectedClient.nif && (
            <div className="text-xs text-neutral-600">
              NIF: {selectedClient.nif}
            </div>
          )}
          {selectedClient.zona && (
            <div className="text-xs text-neutral-600">
              Zona: {selectedClient.zona}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AutocompleteClient;